import React from 'react';
import { FileText, Video, BookOpen, Download, ChevronRight, Calendar, Clock, CheckCircle } from 'lucide-react';

export const ResourcesPage: React.FC = () => {
  const guides = [
    {
      title: "Complete Guide to FDA Accelerated Approval",
      category: "Regulatory Strategy",
      type: "PDF Guide",
      readTime: "15 min read"
    },
    {
      title: "Navigating EMA Orphan Designation in 2024",
      category: "Rare Disease",
      type: "Whitepaper",
      readTime: "20 min read"
    },
    {
      title: "PMDA Consultation Types: A Playbook",
      category: "Japan Market",
      type: "Playbook",
      readTime: "10 min read"
    }
  ];

  const webinars = [
    {
      title: "Preparing for the FDA Type C Meeting",
      date: "Oct 15, 2024",
      speaker: "Dr. Sarah Chen, ex-FDA Reviewer"
    },
    {
      title: "Digital Therapeutics: SaMD Regulatory Pathways",
      date: "Oct 22, 2024",
      speaker: "Mark Johnson, Digital Health Expert"
    }
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero */}
      <div className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-4">Regulatory Intelligence Center</h1>
          <p className="text-xl text-slate-400 max-w-2xl">
            Expert guides, market reports, and webinars to help you navigate the evolving global regulatory landscape.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          
          {/* Main Feed */}
          <div className="lg:col-span-2 space-y-12">
            <section>
              <div className="flex items-center justify-between mb-6">
                 <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                   <BookOpen className="w-6 h-6 text-blue-600"/> Latest Guides & Whitepapers
                 </h2>
                 <a href="#" className="text-blue-600 hover:text-blue-800 font-medium text-sm">View All</a>
              </div>
              <div className="grid gap-6">
                {guides.map((guide, idx) => (
                  <div key={idx} className="flex flex-col md:flex-row gap-6 p-6 border border-slate-200 rounded-xl hover:shadow-md transition-shadow cursor-pointer group">
                    <div className="w-full md:w-48 h-32 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                       <FileText className="w-10 h-10 text-slate-300 group-hover:text-blue-500 transition-colors" />
                    </div>
                    <div>
                       <div className="flex items-center gap-2 text-xs font-medium text-blue-600 uppercase tracking-wide mb-2">
                          <span>{guide.category}</span>
                          <span className="text-slate-300">•</span>
                          <span>{guide.type}</span>
                       </div>
                       <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-700 transition-colors">{guide.title}</h3>
                       <p className="text-slate-500 text-sm mb-4">Comprehensive analysis of the latest guidance documents and success factors for applicants.</p>
                       <div className="flex items-center text-sm text-slate-400">
                          <Clock className="w-4 h-4 mr-1"/> {guide.readTime}
                       </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
               <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6">State of Regulatory Consulting 2024</h2>
                  <div className="flex flex-col md:flex-row gap-8 items-center">
                     <div className="flex-grow">
                        <p className="text-slate-600 mb-6 text-lg">
                           Our annual benchmark report analyzes pricing trends, approval rates, and demand across 500+ consultancies globally.
                        </p>
                        <ul className="space-y-3 mb-8">
                           <li className="flex items-center text-slate-700"><CheckCircle className="w-5 h-5 text-green-500 mr-2"/> Hourly rate benchmarks by region</li>
                           <li className="flex items-center text-slate-700"><CheckCircle className="w-5 h-5 text-green-500 mr-2"/> Orphan designation success rates</li>
                           <li className="flex items-center text-slate-700"><CheckCircle className="w-5 h-5 text-green-500 mr-2"/> Emerging market trends</li>
                        </ul>
                        <button className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors">
                           <Download className="w-4 h-4 mr-2"/> Download Report
                        </button>
                     </div>
                     <div className="w-full md:w-64 h-80 bg-white shadow-lg border border-slate-200 rounded-lg flex items-center justify-center">
                        <div className="text-center">
                           <div className="text-6xl font-bold text-slate-100 mb-2">2024</div>
                           <div className="text-slate-400 font-medium uppercase tracking-widest">Report</div>
                        </div>
                     </div>
                  </div>
               </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
             <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                   <Video className="w-5 h-5 text-purple-600"/> Upcoming Webinars
                </h3>
                <div className="space-y-6">
                   {webinars.map((w, i) => (
                      <div key={i} className="border-b border-slate-100 pb-4 last:border-0 last:pb-0">
                         <div className="text-xs font-bold text-purple-600 mb-1 flex items-center gap-1">
                            <Calendar className="w-3 h-3"/> {w.date}
                         </div>
                         <h4 className="font-medium text-slate-900 mb-1 hover:text-purple-700 cursor-pointer">{w.title}</h4>
                         <p className="text-xs text-slate-500">{w.speaker}</p>
                         <button className="mt-2 text-xs font-medium text-blue-600 hover:text-blue-800">Register Now</button>
                      </div>
                   ))}
                </div>
             </div>

             <div className="bg-blue-50 border border-blue-100 rounded-xl p-6">
                <h3 className="font-bold text-blue-900 mb-2">Subscribe to Intelligence</h3>
                <p className="text-sm text-blue-700 mb-4">Get weekly regulatory updates and new consultancy listings delivered to your inbox.</p>
                <input type="email" placeholder="Work email address" className="w-full p-2 rounded border border-blue-200 text-sm mb-2"/>
                <button className="w-full bg-blue-600 text-white py-2 rounded font-medium text-sm hover:bg-blue-700">Subscribe</button>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};